import React, { useEffect, useState } from "react";
import { Popconfirm, Table, message } from "antd";
import {
  LockOutlined,
  UnlockOutlined,
  SwapOutlined,
  CheckCircleOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { API_URL } from "../../constants";
import "./workingmonth.css";

const MONTHS_RO = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];
const monthLabel = (ln, an) => `${MONTHS_RO[ln - 1]} ${an}`;

const Badge = ({ tone = "neutral", children }) => (
  <span className={`wm-badge wm-badge-${tone}`}>{children}</span>
);

const Btn = ({ variant = "secondary", small, loading, icon, children, ...rest }) => (
  <button
    type="button"
    className={`wm-btn wm-btn-${variant}${small ? " wm-btn-sm" : ""}${
      loading ? " wm-btn-loading" : ""
    }`}
    {...rest}
  >
    {loading ? <span className="wm-spin" /> : icon || null}
    {children}
  </button>
);

const WorkingMonth = () => {
  const [monthState, setMonthState] = useState(null);
  const [logs, setLogs] = useState([]);
  const [blockedClientIndici, setBlockedClientIndici] = useState(false);
  const [loading, setLoading] = useState(false);
  const accessToken = useSelector(
    (state) => state.user.currentUser?.accessToken
  );
  const authHeaders = { headers: { Authorization: "Bearer " + accessToken } };

  const fetchAll = async () => {
    try {
      const [stateRes, logRes, clientRes] = await Promise.all([
        axios.get(API_URL + "user/gestionar/modificari/workingMonth"),
        axios.get(API_URL + "user/gestionar/modificari/monthLog"),
        axios.get(API_URL + "user/gestionar/client/blockageStatus"),
      ]);
      setMonthState(stateRes.data);
      setLogs(logRes.data);
      setBlockedClientIndici(!!clientRes.data.blocked);
    } catch (error) {
      console.log({ error });
      message.error("Nu am putut încărca starea lunii de lucru.");
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const runAction = async (fn) => {
    setLoading(true);
    try {
      await fn();
    } finally {
      setLoading(false);
      await fetchAll();
    }
  };

  const handleCloseMonth = () =>
    runAction(async () => {
      try {
        const res = await axios.post(
          API_URL + "user/gestionar/modificari/closeMonth",
          {},
          authHeaders
        );
        const closed = res.data.closedMonth;
        const wm = res.data.workingMonth;
        message.success(
          `Luna ${monthLabel(closed.LN, closed.AN)} a fost închisă. ` +
            `De acum se lucrează în ${monthLabel(wm.LN, wm.AN)}.`,
          8
        );
      } catch (error) {
        message.error(error.response?.data?.message || error.message, 6);
      }
    });

  const handleOpenMonth = () =>
    runAction(async () => {
      try {
        const res = await axios.post(
          API_URL + "user/gestionar/modificari/openMonth",
          {},
          authHeaders
        );
        const wm = res.data.workingMonth;
        const ab = res.data.abandonedMonth;
        const d = res.data.deleted || {};
        const totalDeleted = Object.values(d).reduce((s, n) => s + (n || 0), 0);
        message.success(
          `Luna ${monthLabel(wm.LN, wm.AN)} a fost deschisă. De acum se lucrează în ea. ` +
            (ab
              ? `Datele introduse în Modificări pentru ${monthLabel(ab.LN, ab.AN)} au fost șterse (${totalDeleted} înregistrări).`
              : ""),
          10
        );
      } catch (error) {
        message.error(error.response?.data?.message || error.message, 6);
      }
    });

  const handleTransferIndici = () =>
    runAction(async () => {
      try {
        const res = await axios.post(
          API_URL + "user/gestionar/modificari/transferIndici",
          {},
          authHeaders
        );
        const t = res.data.targetMonth || res.data.target;
        message.success(
          `Transfer finalizat pentru ${monthLabel(t.LN, t.AN)}: ` +
            `${res.data.missingCount ?? 0} indici completați din luna precedentă.`,
          8
        );
      } catch (error) {
        message.error(error.response?.data?.message || error.message, 6);
      }
    });

  const toggleModificari = () =>
    runAction(async () => {
      try {
        const route = monthState?.blocked
          ? "user/gestionar/modificari/removeBlockage"
          : "user/gestionar/modificari/setBlockage";
        await axios.get(API_URL + route);
        message.success(
          monthState?.blocked
            ? "Compartimentul Modificări a fost deblocat — gestionarii pot lucra."
            : "Compartimentul Modificări a fost blocat."
        );
      } catch (error) {
        message.error(error.message);
      }
    });

  const toggleClientIndici = () =>
    runAction(async () => {
      try {
        const route = blockedClientIndici
          ? "user/gestionar/client/removeBlockage"
          : "user/gestionar/client/setBlockage";
        await axios.get(API_URL + route);
        message.success(
          blockedClientIndici
            ? "Prezentarea indicilor de către clienți a fost permisă."
            : "Prezentarea indicilor de către clienți a fost blocată."
        );
      } catch (error) {
        message.error(error.message);
      }
    });

  const wm = monthState;
  const wmLabel = wm ? monthLabel(wm.ln, wm.an) : "…";
  const realLabel = wm ? monthLabel(wm.realLn, wm.realAn) : "…";
  const prevLabel = wm ? monthLabel(wm.prevLn, wm.prevAn) : "…";
  const lastLog = logs.length ? logs[0] : null;

  // recomandarea "Ce urmeaza"
  let nextText = "Se încarcă starea lunii de lucru…";
  if (wm) {
    if (!wm.isBehind) {
      nextText =
        `Totul e la zi — se lucrează în ${wmLabel}. Pe data de 1 accesul se blochează ` +
        `automat, iar ${wmLabel} rămâne deschisă pentru finalizare până o închizi.`;
    } else if (wm.blocked) {
      nextText =
        `${wmLabel} așteaptă finalizarea, iar accesul gestionarilor e blocat. ` +
        `Transferă indicii, deblochează dacă mai sunt corecturi, apoi închide luna.`;
    } else {
      nextText =
        `Gestionarii lucrează pe ${wmLabel} (văd banner roșu în aplicație). ` +
        `Când corecturile sunt gata, închide luna — se va trece pe ${realLabel}.`;
    }
  }

  // fluxul lunar (dupa logica veche a cron-urilor)
  let currentStep = 0;
  if (wm) {
    if (!wm.isBehind) currentStep = 5;
    else if (wm.blocked) currentStep = 1;
    else currentStep = 2;
  }

  const steps = [
    {
      title: "Data de 1 — blocare automată",
      desc:
        "La 00:00 Modificările și Prezentarea indicilor se blochează automat. " +
        "Luna de lucru rămâne luna trecută — nimic nu se închide de la sine.",
    },
    {
      title: "Transferă indicii",
      desc:
        "Contoarele fără indici prezentați se completează din luna precedentă (consum 0), " +
        "ca datele să fie complete pentru calcul. Rulează EXCLUSIV din buton — nimic automat.",
    },
    {
      title: "Deblochează pentru corecturi",
      desc:
        "Dacă gestionarii mai au de corectat, deblochează Modificările — ei lucrează pe " +
        "luna de lucru, cu banner roșu. Poți bloca și debloca oricând.",
    },
    {
      title: "Calcul și facturare",
      desc:
        "Descarcă modificările / indicii / LUC și importă rezultatele — din " +
        "compartimentul „Documente diverse”, ca până acum.",
    },
    {
      title: "Închide luna",
      desc:
        "Când totul e gata, „Închide luna” trece lucrul pe luna calendaristică curentă. " +
        "La nevoie, „Deschide luna precedentă” te întoarce cu un pas.",
    },
  ];

  const logColumns = [
    {
      title: "Data",
      dataIndex: "createdAt",
      width: 150,
      render: (v) => (
        <span className="wm-log-time">{dayjs(v).format("DD.MM.YYYY HH:mm")}</span>
      ),
    },
    {
      title: "Acțiune",
      dataIndex: "action",
      width: 130,
      render: (v) =>
        v === "close" ? (
          <Badge tone="neutral">Închisă</Badge>
        ) : (
          <Badge tone="good">Deschisă</Badge>
        ),
    },
    {
      title: "Luna",
      width: 150,
      render: (r) => <span className="wm-log-month">{monthLabel(r.LN, r.AN)}</span>,
    },
    {
      title: "Admin",
      dataIndex: "user",
      render: (v) => <span className="wm-log-email">{v || "—"}</span>,
    },
  ];

  return (
    <div className="wm-page">
      {/* ===== HERO ===== */}
      <div className="wm-hero">
        <div className="wm-hero-top">
          <div>
            <div className="wm-eyebrow">Luna de lucru</div>
            <h1 className="wm-month">{wmLabel}</h1>
            <div className="wm-hero-sub">
              Luna calendaristică: <strong>{realLabel}</strong>
              {lastLog ? (
                <>
                  {"  ·  "}Ultima acțiune:{" "}
                  <strong>
                    {lastLog.action === "close" ? "închisă" : "deschisă"}{" "}
                    {monthLabel(lastLog.LN, lastLog.AN)}
                  </strong>{" "}
                  ({dayjs(lastLog.createdAt).format("DD.MM, HH:mm")})
                </>
              ) : null}
            </div>
          </div>

          {wm ? (
            wm.isBehind ? (
              <span className="wm-chip wm-chip-warn">
                <span className="wm-chip-dot" />
                În urmă — calendaristic e {realLabel}
              </span>
            ) : (
              <span className="wm-chip wm-chip-good">
                <span className="wm-chip-dot" />
                La zi cu luna calendaristică
              </span>
            )
          ) : null}
        </div>

        <div className="wm-next">
          <span className="wm-next-label">Ce urmează</span>
          <span className="wm-next-text">{nextText}</span>
          <span className="wm-next-actions">
            {wm?.isBehind && wm.blocked ? (
              <Btn
                variant="hero"
                icon={<UnlockOutlined />}
                loading={loading}
                onClick={toggleModificari}
              >
                Deblochează modificările
              </Btn>
            ) : null}
            {wm?.isBehind && !wm.blocked ? (
              <Popconfirm
                title={`Închideți luna ${wmLabel}?`}
                description={`Din acel moment se va lucra în ${realLabel}. Nu rulează nimic automat.`}
                okText="Da, închide"
                cancelText="Renunță"
                onConfirm={handleCloseMonth}
              >
                <Btn variant="hero" icon={<CheckCircleOutlined />} loading={loading}>
                  Închide luna {wmLabel}
                </Btn>
              </Popconfirm>
            ) : null}
          </span>
        </div>
      </div>

      {/* ===== ACTIONS ===== */}
      <div className="wm-card wm-actions-card">
        <h3 className="wm-card-title">Acțiuni</h3>

        <div className="wm-action">
          <div className="wm-action-info">
            <div className="wm-action-title">
              Acces Modificări — gestionari
              {wm?.blocked ? (
                <Badge tone="bad">Blocat</Badge>
              ) : (
                <Badge tone="good">Activ</Badge>
              )}
            </div>
            <div className="wm-action-desc">
              {wm?.blocked
                ? `Gestionarii NU pot salva nimic acum. Apasă „Deblochează” ca să poată lucra pe ${wmLabel}.`
                : `Gestionarii pot lucra pe ${wmLabel}. Apasă „Blochează” ca să oprești orice salvare.`}
            </div>
          </div>
          <span className="wm-action-btn">
            <Btn
              variant={wm?.blocked ? "success" : "danger"}
              icon={wm?.blocked ? <UnlockOutlined /> : <LockOutlined />}
              loading={loading}
              onClick={toggleModificari}
              disabled={!wm}
            >
              {wm?.blocked ? "Deblochează" : "Blochează"}
            </Btn>
          </span>
        </div>

        <div className="wm-action">
          <div className="wm-action-info">
            <div className="wm-action-title">
              Prezentare indici — clienți
              {blockedClientIndici ? (
                <Badge tone="bad">Blocat</Badge>
              ) : (
                <Badge tone="good">Activ</Badge>
              )}
            </div>
            <div className="wm-action-desc">
              {blockedClientIndici
                ? "Clienții NU pot prezenta indici pe invoicer.md. Apasă „Permite” când începe perioada de prezentare."
                : "Clienții pot prezenta indici pe invoicer.md. Apasă „Blochează” ca să oprești prezentarea."}
            </div>
          </div>
          <span className="wm-action-btn">
            <Btn
              variant={blockedClientIndici ? "success" : "danger"}
              icon={blockedClientIndici ? <UnlockOutlined /> : <LockOutlined />}
              loading={loading}
              onClick={toggleClientIndici}
              disabled={!wm}
            >
              {blockedClientIndici ? "Permite" : "Blochează"}
            </Btn>
          </span>
        </div>

        {wm?.isBehind ? (
          <div className="wm-action">
            <div className="wm-action-info">
              <div className="wm-action-title">Transferă indicii în {wmLabel}</div>
              <div className="wm-action-desc">
                Contoarele din {wmLabel} care NU au indici prezentați primesc valorile
                din {prevLabel}, cu consum 0 — ca datele să fie complete pentru calcul.
                Rulează DOAR când apeși aici; poți repeta fără griji, nu creează duplicate.
              </div>
            </div>
            <span className="wm-action-btn">
              <Popconfirm
                title={`Transferați indicii pentru ${wmLabel}?`}
                description={`Contoarele fără indici primesc valorile din ${prevLabel}, cu consum 0.`}
                okText="Da, transferă"
                cancelText="Renunță"
                onConfirm={handleTransferIndici}
              >
                <Btn variant="secondary" icon={<SwapOutlined />} loading={loading}>
                  Transferă indicii
                </Btn>
              </Popconfirm>
            </span>
          </div>
        ) : null}

        <div className="wm-action">
          <div className="wm-action-info">
            <div className="wm-action-title">Închide luna {wmLabel}</div>
            <div className="wm-action-desc">
              Termină lucrul pe {wmLabel}. Din acel moment gestionarii lucrează pe{" "}
              {realLabel}, bannerul roșu dispare, iar salvările primesc luna {realLabel}.
              Nu rulează nimic automat la închidere.
            </div>
            {wm && !wm.canClose ? (
              <div className="wm-action-why">
                Disponibil după data de 1 — când {wmLabel} devine lună încheiată — sau
                după ce deschizi o lună anterioară.
              </div>
            ) : null}
          </div>
          <span className="wm-action-btn">
            <Popconfirm
              title={`Închideți luna ${wmLabel}?`}
              description={`Din acel moment se va lucra în ${realLabel}. Nu rulează nimic automat.`}
              okText="Da, închide"
              cancelText="Renunță"
              onConfirm={handleCloseMonth}
              disabled={!wm?.canClose}
            >
              <Btn
                variant="primary"
                icon={<CheckCircleOutlined />}
                loading={loading}
                disabled={!wm?.canClose}
              >
                Închide luna
              </Btn>
            </Popconfirm>
          </span>
        </div>

        <div className="wm-action">
          <div className="wm-action-info">
            <div className="wm-action-title">Deschide luna {prevLabel}</div>
            <div className="wm-action-desc">
              Întoarce lucrul cu o lună înapoi: gestionarii vor lucra pe {prevLabel} (cu
              banner roșu în aplicație) până o închizi la loc.{" "}
              <strong>
                Atenție: TOATE datele lunii {wmLabel} din Modificări (generale, indici,
                contoare, LUC, recalcule) vor fi ȘTERSE — inclusiv cele importate din DBF.
              </strong>{" "}
              După ce închizi luna la loc, luna {wmLabel} se reface prin import.
            </div>
            {wm && !wm.canOpen ? (
              <div className="wm-action-why">
                Se poate deschide cel mult o lună în urmă față de luna calendaristică
                ({realLabel}) — luna {prevLabel} e prea departe.
              </div>
            ) : null}
          </div>
          <span className="wm-action-btn">
            <Popconfirm
              title={`Deschideți luna ${prevLabel}?`}
              description={`ATENȚIE: datele introduse în Modificări pentru ${wmLabel} vor fi ȘTERSE definitiv!`}
              okText="Da, deschide și șterge"
              cancelText="Renunță"
              okButtonProps={{ danger: true }}
              onConfirm={handleOpenMonth}
              disabled={!wm?.canOpen}
            >
              <Btn
                variant="secondary"
                icon={<RollbackOutlined />}
                loading={loading}
                disabled={!wm?.canOpen}
              >
                Deschide luna
              </Btn>
            </Popconfirm>
          </span>
        </div>
      </div>

      {/* ===== FLOW + HISTORY ===== */}
      <div className="wm-grid">
        <div className="wm-card">
          <h3 className="wm-card-title">Fluxul lunar</h3>
          <ol className="wm-steps">
            {steps.map((s, i) => (
              <li
                key={i}
                className={`wm-step${i < currentStep ? " wm-step-done" : ""}${
                  i === currentStep ? " wm-step-current" : ""
                }`}
              >
                <span className="wm-step-marker">
                  {i < currentStep ? "✓" : i + 1}
                </span>
                <div>
                  <div className="wm-step-title">{s.title}</div>
                  <div className="wm-step-desc">{s.desc}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="wm-card wm-history">
          <h3 className="wm-card-title">Istoric închideri / deschideri</h3>
          <Table
            rowKey="id"
            size="small"
            columns={logColumns}
            dataSource={logs}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
          />
        </div>
      </div>
    </div>
  );
};

export default WorkingMonth;
